import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useUpdateProjectUsersMutation } from '../lib/queries';
import { QUERY_KEYS } from '../lib/constants';
import { removeUserFromProjectConfirmDialogAtom } from '../atoms/removeUserFromProjectConfirmDialogAtom';
import { PATHS } from '../lib/paths';

export function RemoveUserFromProjectDialog() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [state, setState] = useAtom(removeUserFromProjectConfirmDialogAtom);
  const dialog = useRef<HTMLDialogElement | null>(null);

  const updateProjectUsersMutation = useUpdateProjectUsersMutation();
  const client = useQueryClient();

  const handleClose = () => {
    setState({ isOpen: false, project: undefined });
    dialog.current?.close();
  };

  const onSubmit = () => {
    if (state.project) {
      updateProjectUsersMutation.mutate(
        {
          pid: state.project?.id,
          users: state.project.users.reduce((p, c) => {
            return c.id !== state.user?.id ? [...p, c.id] : p;
          }, [] as string[]),
        },
        {
          onSuccess: () => {
            toast.success(t('project.removeUserConfirmDialog.success'));
            handleClose();
            client.invalidateQueries({ queryKey: [QUERY_KEYS.PROJECT] });
          },
          onError: (error) => {
            const err = error as Error;
            navigate(PATHS.errorPath(err.message));
          },
        },
      );
    }
  };

  useEffect(() => {
    if (!dialog.current?.open && state.isOpen) dialog.current?.showModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpen]);

  return (
    <dialog ref={dialog} className="max-w-xl">
      <h3 className="text-title">{t('project.removeUserConfirmDialog.title')}</h3>
      <span className="whitespace-normal">
        {t('project.removeUserConfirmDialog.description', {
          user: state.user?.name,
          project: state.project?.name,
        })}
      </span>

      <div className="flex justify-end gap-2">
        <button
          className="primary-outline"
          onClick={() => {
            handleClose();
          }}
        >
          {t('common.cancel')}
        </button>
        <button
          className="primary-fill"
          onClick={() => {
            onSubmit();
          }}
        >
          {t('common.yes')}
        </button>
      </div>
    </dialog>
  );
}
